import { z } from 'zod';
import { conditionBandSchema } from './asset.js';
import { listingTypeSchema, saleMechanicSchema, listingAuctionTermsSchema } from './listing.js';
import {
  type BuyerTier,
  type MarketLabel,
  type PriceRange,
  type FmvBandDto,
  priceRangeSchema,
  fmvBandDtoSchema,
  type UnverifiedPricing,
  type VerifiedPricing,
  type AskPricing,
  unverifiedPricingSchema,
  verifiedPricingSchema,
  askPricingSchema,
  pricingSchema,
  type UnverifiedEvidence,
  type VerifiedEvidence,
  unverifiedEvidenceSchema,
  verifiedEvidenceSchema,
  evidenceSchema,
  type LotMemberForBuyer,
  lotMemberForBuyerSchema,
  type AvState,
  avStateSchema,
  type PaymentTerms,
  paymentTermsSchema,
  type ListingCommercialTerms,
  listingCommercialTermsSchema,
  type ListingOwnerTerms,
  listingOwnerTermsSchema,
  type ListingTechnicalDetails,
  listingTechnicalDetailsSchema,
} from './buyer-tier.js';

// Re-exported for backward compatibility — these were originally defined
// here (SIL-1328/SIL-1331) and moved to ./buyer-tier.js (dependency-free) to
// break a circular import with ./listing.js, which also needs them.
export type { BuyerTier, MarketLabel, PriceRange, FmvBandDto, UnverifiedPricing, VerifiedPricing, AskPricing };
export {
  priceRangeSchema,
  fmvBandDtoSchema,
  unverifiedPricingSchema,
  verifiedPricingSchema,
  askPricingSchema,
  pricingSchema,
};
export type { UnverifiedEvidence, VerifiedEvidence, LotMemberForBuyer };
export { unverifiedEvidenceSchema, verifiedEvidenceSchema, evidenceSchema, lotMemberForBuyerSchema };
// SIL-1345 — same re-export discipline: ./buyer-tier.js stays dependency-free,
// and every consumer keeps importing from @siliconx/shared/dto/marketplace.
export type { AvState, PaymentTerms, ListingCommercialTerms, ListingOwnerTerms, ListingTechnicalDetails };
export {
  avStateSchema,
  paymentTermsSchema,
  listingCommercialTermsSchema,
  listingOwnerTermsSchema,
  listingTechnicalDetailsSchema,
};

/**
 * Buyer marketplace DTOs (RUF-310).
 *
 * This is the BUYER-SAFE projection of a `listing`. It is deliberately a separate,
 * narrower shape than the seller-facing `listingSchema` (dto/listing.ts): a buyer
 * must see ONLY the asking price, listing type, and a minimal descriptive snapshot
 * captured onto the listing at create time (migration 523).
 *
 * Hard exclusions — these never appear here:
 *   * customerOrgId / any seller identity
 *   * FMV low / median / high / confidence
 *   * raw asset fields (serial, data center, class_attributes, …)
 *
 * The descriptive fields (displayTitle, assetClassId, conditionBand) are read
 * straight off the listing row, because the underlying `asset` is unreachable to a
 * buyer (asset RLS isolates on the seller's customer_org).
 */
/**
 * MarketplaceListingBase: the current buyer-safe listing snapshot minus
 * askingPriceUsd, which moves into the tier-tagged `pricing` field (SIL-1328).
 */
export const marketplaceListingBaseSchema = z.object({
  id: z.string().uuid(),
  listingType: listingTypeSchema,
  currencyCode: z.string(),
  /** Quantity for a sku listing; null for an asset listing (implied 1). */
  quantity: z.number().int().nullable(),
  /** Human label captured at create. Null only for legacy rows that failed backfill. */
  displayTitle: z.string().nullable(),
  /** Asset taxonomy id, e.g. 'server' / 'gpu'. */
  assetClassId: z.string().nullable(),
  /** Physical condition band; null when the asset was never condition-assessed. */
  conditionBand: z.string().nullable(),
  /** SKU catalog reference for this listing (null for unmatched asset listings). */
  skuId: z.string().uuid().nullable(),
  /**
   * Manufacturer id snapshot (RUF-332). Denormalized from the linked SKU at
   * listing create time. Buyer-safe hardware fact; never derived from FMV.
   * e.g. 'dell', 'hpe', 'nvidia'. Null for legacy listings or unmatched assets.
   */
  manufacturerId: z.string().nullable(),
  /**
   * Hardware generation snapshot (RUF-332). Denormalized from sku.generation
   * at listing create time. e.g. 'Gen10+', 'Ampere'. Null when not set on SKU.
   */
  generation: z.string().nullable(),
  /**
   * Preference match score (RUF-335). 0 = no pref matched; 1 = one dim matched
   * (asset class OR manufacturer); 2 = both dims matched. Never hides listings —
   * only used to soft-rank matches ahead of non-matches. A buyer with no saved
   * prefs (empty pref_asset_classes / pref_manufacturers) receives 0 for every
   * listing, preserving default created_at DESC order with no badges shown.
   */
  matchScore: z.number().int(),
  /**
   * 669: forward availability — NULL = available now. A future date renders as
   * an "Available <Mon YYYY>" badge; buyers may filter with availability=now.
   */
  availableOn: z.string().nullable(),
  /** SIL-643: buy_now (default) or auction. */
  saleMechanic: saleMechanicSchema,
  /**
   * SIL-643: non-null only for saleMechanic='auction' — drives the browse-card
   * countdown to close. reservePriceUsd is deliberately absent (never exposed
   * to buyers; enforced at the DB column-grant layer too, see 759).
   */
  auction: listingAuctionTermsSchema.nullable(),
  /**
   * SIL-1345: `listing.created_at`. The card's "Listed <date>" line. Buyer-safe
   * on both tiers — how long a listing has sat is a market fact, not seller
   * identity.
   */
  listedAt: z.string(),
  /**
   * SIL-1345: whether the listing will take an offer right now. Mirrors the
   * ACTUAL gate in POST /api/client/offers (`status` must be active or
   * under_offer) rather than re-deriving a second, drifting rule — the card must
   * not render "Make an offer" on a listing the API would 409.
   */
  acceptingOffers: z.boolean(),
  /**
   * SIL-1345: buyer-safe commercial terms, both tiers. Each field is null when
   * the seller did not state it, and the UI hides that tile. `minOfferUsd` is
   * NOT here and never will be — it is seller-private (migration 962).
   */
  ...listingCommercialTermsSchema.shape,
  /**
   * SIL-1345: technical-detail tiles from the SKU's `class_attributes`. Keys for
   * attributes the SKU does not carry are OMITTED, not null — an empty object
   * means "no tiles".
   */
  technicalDetails: listingTechnicalDetailsSchema,
  /**
   * SIL-1345: the three-valued Active Verification state, both tiers.
   *
   * It sits at TOP LEVEL here, not inside an evidence block, because a
   * marketplace row carries no evidence union at all (only `pricing`) — there
   * is nowhere else to put it. On `listingDetailSchema` the same value lives at
   * `evidence.avState`, where the evidence union does exist. Both come from the
   * one source, `listing_av_state()` (migration 962).
   *
   * The card MUST distinguish 'expired' and 'none' from a failure, and may not
   * use the Verified mark for either.
   */
  avState: avStateSchema,
  /**
   * SIL-1347: the tier-tagged evidence block, through the SAME
   * `projectEvidence()` the detail route uses. SIL-1345 deliberately left this
   * to the card slice, shipping only the top-level `avState` it could not
   * render without.
   *
   * Both tiers get a block; the tiers differ in what is IN it, not in whether
   * it exists — unverified gets booleans, the node count and the region;
   * verified additionally gets the site, the per-run pass/obs/fail counts and
   * the custody summary. `avState` is duplicated at `evidence.avState` because
   * the evidence union carries it on the detail route too and a consumer
   * reading one shape must not have to know which screen it came from; both
   * values come from the one `listing_av_state()` call.
   */
  evidence: evidenceSchema,
  /**
   * SIL-1444: distinct `asset_class_id` slugs behind this listing, the
   * server's single resolution of "single/sku listing -> its own
   * `assetClassId`, lot listing -> the union of its members' asset
   * classes" (a lot's own `assetClassId` column is null — RUF-376 lot
   * listings can mix classes). Both the marketplace's asset-type filter
   * options and each card's asset-type badges read this SAME array, so the
   * two surfaces cannot drift on what a listing's asset type(s) are.
   * Buyer-safe on both tiers — sourced from `listing_buyer_evidence()`'s
   * ungated `asset_class_ids` (migration 975).
   */
  assetTypeIds: z.array(z.string()),
  /**
   * SIL-1451: the SKU's resolved catalogue image (own, else its family
   * master's - `v_sku_primary_image`), with the provenance the licence may
   * require showing. `url` is CDN base + object key, resolved server-side.
   * Null when the SKU and its family have no image, or the listing has no
   * SKU (unmatched asset, lot). Optional so a card tolerates an API one
   * version behind (the SIL-1377 skew rule): absent renders generic art.
   */
  image: z
    .object({
      url: z.string(),
      source: z.string().nullable(),
      license: z.string().nullable(),
      attribution: z.string().nullable(),
      sourceUrl: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type MarketplaceListingBase = z.infer<typeof marketplaceListingBaseSchema>;

export const marketplaceListingSchema = marketplaceListingBaseSchema.extend({
  pricing: pricingSchema,
});
export type MarketplaceListing = Omit<z.infer<typeof marketplaceListingBaseSchema>, 'evidence'> & {
  pricing: VerifiedPricing | UnverifiedPricing | AskPricing;
  evidence: VerifiedEvidence | UnverifiedEvidence;
};

export const marketplaceListResponseSchema = z.object({
  data: z.array(marketplaceListingSchema),
});
export type MarketplaceListResponse = z.infer<typeof marketplaceListResponseSchema>;

/**
 * Query-param filter schema for GET /api/client/marketplace (RUF-334).
 *
 * All fields are optional — omitting a param widens results (no filter applied).
 * Combined filters AND together. Invalid values (bad enum, negative price, min>max)
 * are REJECTED with 400 rather than silently ignored.
 *
 * Deferred (not in v1): region, specs, service dimensions.
 */
/**
 * SIL-1347 — a repeatable query param, as the marketplace filter card's
 * checkbox groups send them (`?condition=like_new&condition=high_wear`).
 *
 * Hono's query validator hands a single occurrence through as a STRING and two
 * or more as an array (`hono/validator`: `v.length === 1 ? v[0] : v`), so a
 * plain `z.array(...)` rejects exactly the one-checkbox case — the most common
 * one. Normalising here rather than at each call site is what keeps the route,
 * the MCP tool and the portal from each inventing their own fix.
 *
 * Values within a group OR together; the groups AND (design handoff section 1).
 */
const repeatable = <T extends z.ZodTypeAny>(item: T) =>
  z
    .preprocess(
      (v) => (Array.isArray(v) ? v : typeof v === 'string' && v.length > 0 ? [v] : v),
      z.array(item).min(1),
    )
    .optional();

export const marketplaceFilterSchema = z
  .object({
    /**
     * SIL-1347 — free-text search. Matches the listing id, the display title,
     * the generation and the SKU's GPU/server model (design handoff section 1:
     * "matches ID, model, site and server"). Buyer-safe on both tiers: it
     * narrows rows the caller can already see and reveals no field the
     * projection would have withheld.
     */
    q: z.string().trim().min(1).max(200).optional(),
    /** SIL-1347 — GPU model checkbox group, from the SKU's `class_attributes`. */
    model: repeatable(z.string().min(1)),
    /** SIL-1347 — listing-type checkbox group (Lot / Single node / SKU quantity). */
    type: repeatable(listingTypeSchema),
    /**
     * SIL-1347 — condition checkbox group. The multi-valued sibling of
     * `condition_band` below, which stays for the existing single-select
     * callers (the MCP `marketplace_search` tool). Both may be sent; they AND.
     */
    condition: repeatable(conditionBandSchema),
    /**
     * SIL-1347 — region checkbox group. Region is the ONLY location field an
     * unverified buyer receives (the site is verified-only), and it is not a
     * `listing` column: it resolves through `listing_buyer_evidence()`, so the
     * route applies this filter to that function's output rather than as a
     * WHERE predicate.
     */
    region: repeatable(z.string().min(1)),
    /**
     * SIL-1444 — asset-type checkbox group (`asset_class_id` slugs present in
     * the marketplace, 'cloud_vm' excluded from the CARD's offered options
     * but not rejected here — the route applies this the same way `region`
     * is applied, against `listing_buyer_evidence()`'s (975) ungated
     * `asset_class_ids`, not a `listing` column WHERE predicate, because a
     * lot listing's own `asset_class_id` is null. A lot matches if ANY
     * member asset has a selected type; values within the group OR, and this
     * group ANDs with every other group same as the rest.
     */
    asset_type: repeatable(z.string().min(1)),
    /** Narrow to listings from a specific manufacturer (denormalized from SKU). */
    manufacturer_id: z.string().uuid().optional(),
    /** Narrow to a specific asset class (e.g. 'server', 'gpu'). */
    asset_class_id: z.string().optional(),
    /** Narrow to a specific condition band. */
    condition_band: conditionBandSchema.optional(),
    /** Inclusive lower bound on asking price (USD). */
    price_min: z.coerce.number().nonnegative().optional(),
    /** Inclusive upper bound on asking price (USD). */
    price_max: z.coerce.number().nonnegative().optional(),
    /** Narrow to a specific SKU catalog entry. */
    sku_id: z.string().uuid().optional(),
    /** Narrow to a hardware generation string (e.g. 'Gen10+', 'Ampere'). */
    generation: z.string().optional(),
    /**
     * 669: 'now' = only listings available today (available_on IS NULL or
     * already reached). Omitted = all listings including forward-dated ones.
     */
    availability: z.enum(['now']).optional(),
  })
  .refine(
    (f) =>
      f.price_min === undefined ||
      f.price_max === undefined ||
      f.price_min <= f.price_max,
    { message: 'price_min must be <= price_max', path: ['price_min'] },
  );

export type MarketplaceFilter = z.infer<typeof marketplaceFilterSchema>;
